import type * as api from '@gitbook/api-client';
import { parseMarkdown } from '@gitbook/doc-format';
import type { Document } from '@gitbook/slate';
import { toast } from '@gitbook/spine-react';
import { throwIfNotExposable } from '@gitbook/util-debug';

import { apiResource, useAPIStream } from '../../state';

/**
 * Wrapper for useStreamAPI for the askInOrganization endpoint.
 * It also exports the proper type for the result, which includes the parsed document.
 */
export function useStreamAskInOrganization(
    target: { organization: string },
    query: { query: string }
) {
    return useAPIStream(
        !process.env.GITBOOK_PREDICTABLE_UI
            ? apiResource('streamAskInOrganization', {
                  organizationId: target.organization,
                  query: query.query,
                  format: 'markdown',
                  details: true,
              })
            : null,
        {
            onError: (error) => {
                toast.error('Error while getting search result', error);
                throwIfNotExposable(error);
            },
            transform: async (data): Promise<StreamAskInOrganizationResult | undefined> => {
                const { answer } = data;
                if (!answer) {
                    return answer;
                }

                if (answer.answer && 'markdown' in answer.answer) {
                    const { document } = await parseMarkdown(answer.answer.markdown);
                    return { ...answer, document };
                }

                return answer;
            },
        }
    );
}

export type StreamAskInOrganizationResult =
    | api.SearchAIAnswer
    | (api.SearchAIAnswer & { document: Document });
